import ActBadges from '../../components/ui/ActBadges'
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { Button } from '../../components/ui'
import Icon from '../../components/Icon'
import { DetailPageHeader, DetailSection, InfoGrid, KpiStrip, ProgressMini } from './detail-pages/DetailPageScaffold'
import { normalizeDate } from '../../utils/dateUtils'
import { formatRub, formatRubTable, parseAmount } from '../../utils/formatRub'
import './Company.css'

const PRODUCTION_DEPARTMENTS = new Set(['АР', 'КЖ', 'ИНЖ', 'Инженеры', 'ПБ', 'ГИП', 'ДИЗ', 'Дизайнеры'])

function getActAmountAfterTax(act) {
  const afterTax = parseAmount(act?.amount_after_tax)
  return afterTax == null ? (parseAmount(act?.amount) || 0) : afterTax
}

function getActAmountGross(act) {
  const gross = parseAmount(act?.amount)
  return gross == null ? getActAmountAfterTax(act) : gross
}

function buildStageBudgetGroups(stageIncome, stageCost, formatValue = formatRub) {
  const groups = {
    production: { title: 'Производство', departments: [], amount: 0 },
    service: { title: 'Обслуживание', departments: [], amount: 0 },
  }

  ;(stageIncome?.departments || []).forEach((dept) => {
    const name = dept.department_name || 'Без подразделения'
    const key = PRODUCTION_DEPARTMENTS.has(name) ? 'production' : 'service'
    const amount = Number(dept.total_income || 0)
    if (amount <= 0) return
    groups[key].departments.push(name)
    groups[key].amount += amount
  })

  ;(stageIncome?.other_expenses || []).forEach((expense) => {
    const amount = Number(expense.amount || 0)
    if (amount <= 0) return
    groups.service.departments.push(expense.expense_type || 'Прочие расходы')
    groups.service.amount += amount
  })

  return Object.values(groups)
    .filter((group) => group.amount > 0)
    .map((group) => {
      const pct = Number(stageCost || 0) > 0 ? Math.round((group.amount / Number(stageCost)) * 1000) / 10 : 0
      const sub = group.title === 'Производство'
        ? `${group.departments.length} подразделений`
        : group.departments.length <= 3
          ? group.departments.join(', ')
          : `${group.departments.slice(0, 3).join(', ')} и др.`
      return {
        ...group,
        sub,
        pct: `${pct}%`,
        amountLabel: formatValue(group.amount),
        tone: group.title === 'Производство' ? 'production' : 'service',
      }
    })
}

function getActTitle(act) {
  const rawNumber = act?.act_number || act?.number
  if (!rawNumber) return 'Акт'
  const value = String(rawNumber).trim()
  return /^акт/i.test(value) ? value : `Акт №${value}`
}

function formatActsCount(count) {
  const n = Math.abs(Number(count) || 0)
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${count} акт`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} акта`
  return `${count} актов`
}

function StageCard() {
  const { id: idParam } = useParams()
  const id = idParam ? decodeURIComponent(idParam) : ''
  const navigate = useNavigate()
  const { user } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [completionModalOpen, setCompletionModalOpen] = useState(false)
  const [completionDate, setCompletionDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [completionSubmitting, setCompletionSubmitting] = useState(false)
  usePageHeader({ title: null, hideTopHeader: true }, [])

  const stage = data?.stage
  const contract = data?.contract
  const acts = data?.acts || []
  const stageIncome = data?.stage_income

  const canUpdateStage = user?.permissions?.['stages.update']
  const canCreateAct = user?.permissions?.['acts.create']

  useEffect(() => {
    fetchStageCard()
  }, [id])

  const fetchStageCard = async () => {
    if (!id) {
      setLoading(false)
      setData(null)
      return
    }
    try {
      setLoading(true)
      const response = await api.get(`/company/stages/${encodeURIComponent(id)}/card`)
      if (response.data) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Error fetching stage card:', error)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU')
  }

  const formatStageLabel = (stageData, contractData) => {
    const contractNumber = (stageData?.contract_number || contractData?.number || '').trim()
    const stageType = (stageData?.distribution_type || stageData?.type || '').trim()
    if (contractNumber && stageType) return `${contractNumber} - ${stageType}`
    return stageType || contractNumber || (stageData?.id || '-')
  }

  const menuIcon = (name) => <Icon name={name} size={14} />

  const openCompletionModal = () => {
    setCompletionDate(new Date().toISOString().slice(0, 10))
    setCompletionModalOpen(true)
  }

  const submitCompletion = async () => {
    if (!id || !stage) return
    setCompletionSubmitting(true)
    setMessage({ type: '', text: '' })
    try {
      await api.put(`/company/stages/${encodeURIComponent(id)}`, {
        object_stage_id: stage.object_stage_id,
        type: stage.type || stage.name || stage.id,
        cost: stage.cost,
        contract_cost: stage.contract_cost,
        contract_cost_no_vat: stage.contract_cost_no_vat,
        planned_date: normalizeDate(stage.planned_date) || null,
        fact_date: normalizeDate(stage.fact_date) || null,
        completion_date: completionDate,
        distribution_type: stage.distribution_type,
        execution_days: stage.execution_days,
        stage_type_id: stage.stage_type_id,
        status: 'работы завершены',
        custom_data: stage.custom_data || {}
      })
      setCompletionModalOpen(false)
      await fetchStageCard()
      setMessage({ type: 'success', text: 'Статус изменён на «работы завершены», дата завершения сохранена.' })
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось сохранить' })
    } finally {
      setCompletionSubmitting(false)
    }
  }

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  if (!data || !stage) {
    return <div className="no-data">Стадия не найдена</div>
  }

  const contractId = contract?.id
  const stageActsTotal = acts.reduce((sum, act) => sum + getActAmountGross(act), 0)
  const stageBudgetGroups = buildStageBudgetGroups(stageIncome, stage.cost, formatRub)
  const stageProgressPercent = Number(stage.cost || 0) > 0 ? Math.round((stageActsTotal / Number(stage.cost || 0)) * 1000) / 10 : 0

  return (
    <div className="company-page entity-detail-page entity-detail-redesign stage-detail-redesign">
      <DetailPageHeader
        breadcrumbs={[
          { label: 'Клиенты', to: '/company/clients' },
          { label: contract?.client_name || 'Клиент', to: contract?.client_id ? `/client/${contract.client_id}` : undefined },
          { label: contract?.number || 'Договор', to: contractId ? `/contract/${contractId}` : undefined },
          { label: stage.distribution_type || stage.type || 'Стадия' }
        ]}
        title={stage.custom_data?.correction?.name || stage.distribution_type || stage.type || formatStageLabel(stage, contract)}
        status={stage.status || 'в работе'}
        subtitle={`Договор ${contract?.number || '—'} — ${contract?.client_name || 'Клиент'} — ${contract?.object_name || 'Объект'}`}
        onBack={() => navigate('/company/stages')}
        actions={[
          stage.custom_data?.correction ? {id:'correction-plan',label:'Распределение корректировки',onSelect:()=>navigate(`/company/corrections/${encodeURIComponent(id)}`)} : null,
          canUpdateStage && contractId ? {
            id: 'edit-stage',
            label: 'Редактировать стадию',
            icon: menuIcon('edit'),
            onSelect: () => navigate(stage.custom_data?.correction ? `/company/corrections/${encodeURIComponent(id)}/edit` : `/company/contracts/${contractId}/stages/${encodeURIComponent(id)}/edit`)
          } : null,
          {
            id: 'stage-report',
            label: 'Сформировать отчёт по стадии',
            icon: menuIcon('file'),
            onSelect: () => navigate(`/stage/${encodeURIComponent(id)}/report-preview`)
          },
          {
            id: 'stage-income-report',
            label: 'Сформировать отчёт по доходам',
            icon: menuIcon('chart'),
            onSelect: () => navigate(`/stage/${encodeURIComponent(id)}/income-report`)
          },
          { id: 'stage-action-divider', type: 'divider' },
          canUpdateStage && !['работы завершены', 'закрыта'].includes((stage.status || '').toLowerCase().trim()) ? {
            id: 'complete-stage',
            label: 'Завершить работы',
            icon: menuIcon('check'),
            tone: 'success',
            onSelect: openCompletionModal
          } : null
        ]}
      />

      {completionModalOpen && (
        <div className="modal-overlay" onClick={() => !completionSubmitting && setCompletionModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="completion-modal-title">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 id="completion-modal-title">Работы завершены</h3>
            <p style={{ marginBottom: 'var(--spacing-md)' }}>Укажите дату завершения (по умолчанию — сегодня):</p>
            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
              <label htmlFor="stage-completion-date" style={{ display: 'block', marginBottom: 'var(--spacing-xs)' }}>Дата завершения</label>
              <input
                id="stage-completion-date"
                type="date"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                className="ui-input"
                style={{ minWidth: '160px' }}
              />
            </div>
            <div className="modal-actions">
              <Button type="button" variant="secondary" onClick={() => !completionSubmitting && setCompletionModalOpen(false)} disabled={completionSubmitting}>
                Отмена
              </Button>
              <Button type="button" variant="primary" onClick={submitCompletion} disabled={completionSubmitting}>
                {completionSubmitting ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {message.text && (
        <div className={`message message-${message.type}`} role="alert">
          {message.text}
          <button
            type="button"
            className="message-close"
            onClick={() => setMessage({ type: '', text: '' })}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
      )}

      <DetailSection title="Паспорт стадии">
        <InfoGrid
          items={[
            { label: 'Название', value: stage.distribution_type || stage.type || formatStageLabel(stage, contract) },
            { label: 'Договор', value: <button type="button" className="entity-text-link" onClick={() => contractId && navigate(`/contract/${contractId}`)}>{contract?.number || '-'}</button> },
            { label: 'Клиент', value: <button type="button" className="entity-text-link" onClick={() => contract?.client_id && navigate(`/client/${contract.client_id}`)}>{contract?.client_name || '-'}</button> },
            { label: 'Объект', value: <button type="button" className="entity-text-link" onClick={() => contract?.object_id && navigate(`/object/${contract.object_id}`)}>{contract?.object_name || '-'}</button> },
            { label: 'Плановая дата начала', value: formatDate(stage.planned_date) },
            { label: 'Срок по договору', value: formatDate(stage.fact_date) },
            { label: 'Стоимость', value: <span className="amount-mono">{formatRub(stage.cost)}</span> },
            { label: 'Стоимость по договору', value: <span className="amount-mono">{formatRub(stage.contract_cost)}</span> },
            stage.completion_date ? { label: 'Дата завершения', value: formatDate(stage.completion_date) } : null,
            { label: 'Тип распределения', value: stage.distribution_type || '-' },
          ]}
        />
      </DetailSection>

      <KpiStrip
        items={[
          { label: 'Стоимость', value: formatRub(stage.cost), tone: 'accent' },
          { label: 'Заактировано', value: formatRub(stageActsTotal), tone: 'success', meta: formatActsCount(acts.length) },
          { label: 'Остаток', value: formatRub(Math.max(0, Number(stage.cost || 0) - stageActsTotal)), tone: 'info' },
          { label: 'Срок сдачи', value: formatDate(stage.fact_date), tone: 'warning' },
        ]}
      />

      <div className="contract-two-col">
        <div className="section-card">
          <div className="section-card-header section-card-header-actions">
            <h2>Бюджет</h2>
            <Button
              type="button"
              variant="secondary"
              className="btn-add"
              onClick={() => navigate(`/stage/${encodeURIComponent(id)}/report-preview`)}
            >
              Подробнее
            </Button>
          </div>
          <div className="stage-budget-progress">
            <div className="progress-label">
              <span className="progress-label-left">Заактировано</span>
              <span className="progress-label-right">
                {stageProgressPercent.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%
              </span>
            </div>
            <ProgressMini
              percent={stageProgressPercent}
              tone="green"
            />
          </div>
          {stageBudgetGroups.length === 0 ? (
            <p className="info-note">Бюджет стадии не найден</p>
          ) : (
            <div className="stage-budget-link-list">
              {stageBudgetGroups.map((group) => (
                <button
                  key={group.title}
                  type="button"
                  className={`stage-budget-link-card stage-budget-link-card--${group.tone}`}
                  onClick={() => navigate(`/stage/${encodeURIComponent(id)}/report-preview`)}
                >
                  <div className="stage-budget-link-card__left">
                    <div className="stage-budget-link-card__icon">
                      <Icon name="chart" size={18} />
                    </div>
                    <div>
                      <div className="stage-budget-link-card__title">{group.title}</div>
                      <div className="stage-budget-link-card__sub">{group.sub}</div>
                    </div>
                  </div>
                  <div className="stage-budget-link-card__right">
                    <div className="stage-budget-link-card__amount">{group.amountLabel}</div>
                    <div className="stage-budget-link-card__pct">{group.pct}</div>
                  </div>
                  <span className="stage-budget-link-card__arrow" aria-hidden>›</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="section-card">
          <div className="section-card-header section-card-header-actions">
            <h2>Акты</h2>
            {canCreateAct && contractId && (
              <Button
                onClick={() => navigate(`/company/contracts/${contractId}/acts/new`, { state: { legacyStageId: stage.id, returnTo: `/stage/${encodeURIComponent(id)}`, returnLabel: '← Назад к бюджету' } })}
                variant="primary"
                className="btn-add"
              >
                + Добавить акт
              </Button>
            )}
        </div>
        {acts.length > 0 ? (
          <div className={`stage-act-list${acts.length > 7 ? ' contract-scroll-list' : ''}`}>
            {acts.map((act) => (
              <div
                key={act.id}
                role="button"
                tabIndex={0}
                className="stage-act-row"
                onClick={() => navigate(`/act/${act.id}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    navigate(`/act/${act.id}`)
                  }
                }}
              >
                <div className="stage-act-row__info">
                  <span className="stage-act-row__number">{getActTitle(act)}</span>
                  <span className="stage-act-row__date">{formatDate(act.issue_date)}</span>
                </div>
                <div className="stage-act-row__side">
                  <span className="stage-act-row__amount">{formatRubTable(getActAmountGross(act))}</span>
                  <ActBadges act={act} />
                </div>
              </div>
            ))}
            <div className="stage-act-total">
              <span>Итого по актам</span>
              <strong>{formatRubTable(stageActsTotal)}</strong>
            </div>
          </div>
        ) : (
          <div className="contract-acts-empty">
            <Icon name="act" size={40} />
            <p>Акты не найдены</p>
          </div>
        )}
        </div>
      </div>

    </div>
  )
}

export default StageCard
